"use client";

import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import SignAgreement from "./components/SignAgreement";

import ConditionPDFtoExcel from "./components/ConditionPDFtoExcel";
import {
  FiHome,
  FiUser,
  FiEdit,
  FiBriefcase,
  FiTool,
  FiFolderPlus,
  FiDollarSign,
  FiFileText,
  FiTable,
  FiBarChart,
  FiClock,
  FiCreditCard,
  FiSettings,
  FiCoffee,
  FiCodesandbox,
  FiDatabase,
  FiMap,
  FiPieChart, // ← add
  FiSend, // ← add
  FiPenTool,
} from "react-icons/fi";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from "@headlessui/react";
import {
  Bars3Icon,
  BellIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { BsArrowUpCircleFill, BsFillCartPlusFill } from "react-icons/bs";
import { FaClipboardQuestion } from "react-icons/fa6";
import { FaExchangeAlt } from "react-icons/fa";

import SignIn from "./SignIn";
import Callback from "./components/Callback";
import {
  msalInstance,
  initializeMsal,
  reauthenticateWithBackend,
} from "./authConfig";

import { TabProvider, useTabs } from "./components/TabContext";
import TabbedWorkspace from "./components/TabbedWorkspace";

import logo from "./assets/bldera.png";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Axios interceptor for token refresh and tenant ID
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await reauthenticateWithBackend();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          const tenantId = localStorage.getItem("tenantId");
          if (tenantId) {
            originalRequest.headers["X-Tenant-ID"] = tenantId;
          }
          return axios(originalRequest);
        }
        console.warn("Interceptor: No new token, redirecting to /login");
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("token");
        localStorage.removeItem("tenantId");
        window.location.href = "/login";
      } catch (err) {
        console.error("Interceptor: Token refresh failed:", err);
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("token");
        localStorage.removeItem("tenantId");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// the signed-in person's name, for the takeoff change log ("who changed what")
function rememberUserName(u) {
  const name =
    u?.displayName ||
    [u?.givenName, u?.surname].filter(Boolean).join(" ") ||
    u?.email ||
    "";
  if (name) localStorage.setItem("userDisplayName", name);
}

export default function App() {
  return <MainApp />;
}

function MainApp() {
  const [user, setUser] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(
    localStorage.getItem("desktopSidebarOpen") === "true",
  );
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [activeNotificationTab, setActiveNotificationTab] = useState("All");
  const [tenantId, setTenantId] = useState(localStorage.getItem("tenantId"));
  const pathname = window.location.pathname;

  useEffect(() => {
    // Public signing page: no MSAL, no session check, no redirects
    if (window.location.pathname.startsWith("/sign/")) {
      setIsAuthChecked(true);
      return;
    }

    async function setupMsal() {
      try {
        await initializeMsal();
        setMsalInitialized(true);
        if (window.location.pathname !== "/callback") {
          if (localStorage.getItem("isLoggedIn") === "true") {
            await checkUserSession();
          }
          // SignIn renders automatically when there is no user
        }
      } catch (error) {
        console.error("MSAL setup failed:", error);
      } finally {
        setIsAuthChecked(true);
      }
    }
    setupMsal();
  }, []);

  // Check user session with backend JWT
  // Check user session with backend JWT
  async function checkUserSession() {
    try {
      const token = localStorage.getItem("token");
      const tenantId = localStorage.getItem("tenantId");
      if (!token || !tenantId) {
        console.warn("No token or tenantId found");
        setUser(null);
        localStorage.removeItem("userRole");
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("token");
        localStorage.removeItem("tenantId");
        localStorage.removeItem("userDisplayName");
        return;
      }

      const [userResponse, companyResponse] = await Promise.all([
        axios.get(
          "https://erp-production-72da01c8e651.herokuapp.com/api/auth/me",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Tenant-ID": tenantId,
            },
          },
        ),
        axios.get(
          "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Tenant-ID": tenantId,
            },
          },
        ),
      ]);

      if (userResponse.data.success) {
        setUser(userResponse.data.user);
        localStorage.setItem("userRole", userResponse.data.user.userRole);
        rememberUserName(userResponse.data.user);
        setCompanyProfile(companyResponse.data);
        setTenantId(tenantId);
      } else {
        throw new Error("Invalid session");
      }
    } catch (error) {
      console.error("Session check failed:", error);
      setUser(null);
      setCompanyProfile(null);
      setTenantId(null);
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("token");
      localStorage.removeItem("userRole");
      localStorage.removeItem("tenantId");
      localStorage.removeItem("userDisplayName");
    }
  }

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem("desktopSidebarOpen", desktopSidebarOpen);
  }, [desktopSidebarOpen]);

  // Fetch notifications
  useEffect(() => {
    if (user && tenantId) {
      const token = localStorage.getItem("token");
      axios
        .get(
          "https://erp-production-72da01c8e651.herokuapp.com/api/users/notifications",
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-User-ID": user.id,
              "X-Tenant-ID": tenantId,
            },
          },
        )
        .then((res) => {
          setNotifications(res.data.notifications || []);
        })
        .catch((err) => console.error("Error fetching notifications:", err));
    }
  }, [user, tenantId]);

  // Handle login
  // Handle login
  const handleLogin = async (userData) => {
    setUser(userData);
    rememberUserName(userData);
    localStorage.setItem("isLoggedIn", "true");
    try {
      const token = localStorage.getItem("token");
      const tenantId = localStorage.getItem("tenantId");
      if (!token || !tenantId) {
        console.warn("No token or tenantId found after login");
        setUser(null);
        localStorage.removeItem("isLoggedIn");
        return;
      }

      const response = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenantId,
          },
        },
      );

      setCompanyProfile(response.data);
      setTenantId(tenantId);
    } catch (err) {
      console.error("Error fetching company profile:", err);
      setUser(null);
      localStorage.removeItem("isLoggedIn");
    }
  };

  // Handle logout
  const handleLogout = () => {
    setUser(null);
    setCompanyProfile(null);
    setTenantId(null);
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("token");
    localStorage.removeItem("tenantId");
    localStorage.removeItem("givenName");
    localStorage.removeItem("surname");
    localStorage.removeItem("userRole");
    sessionStorage.removeItem("erp_open_tabs");

    localStorage.removeItem("lastVisitedRoute");
    msalInstance.setActiveAccount(null);
    msalInstance
      .logoutRedirect()
      .catch((e) => console.error("Logout error:", e));
    window.location.href = "/login";
  };

  // Mark notification as read
  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const tenantId = localStorage.getItem("tenantId");
      if (!token || !tenantId) {
        console.warn("No token or tenantId for marking notification, skipping");
        return;
      }

      await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/users/notifications/${id}/read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-User-ID": user.id,
            "X-Tenant-ID": tenantId,
          },
        },
      );

      setNotifications((prev) =>
        prev.map((n) =>
          n._id === id ? { ...n, read: true, readAt: new Date() } : n,
        ),
      );
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  // Get notification icon
  const getNotificationIcon = (category, read) => {
    const iconClass = classNames(
      read ? "text-gray-400" : "text-orange-400",
      "h-5 w-5 shrink-0",
    );
    switch (category) {
      case "materials":
        return <BsFillCartPlusFill className={iconClass} />;
      case "rfis":
        return <FaClipboardQuestion className={iconClass} />;
      case "change_orders":
        return <FaExchangeAlt className={iconClass} />;
      case "time_and_materials":
      case "other":
        return <BsArrowUpCircleFill className={iconClass} />;
      default:
        return <BsArrowUpCircleFill className={iconClass} />;
    }
  };

  // Format relative time for notifications
  const formatRelativeTime = (date) => {
    const now = new Date();
    const diffInMs = now - new Date(date);
    const seconds = Math.floor(diffInMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return "Just now";
  };

  // Callback route stays full-page, outside the tab system
  if (pathname === "/callback") {
    return <Callback onLogin={handleLogin} />;
  }

  // Public subcontractor signing page: no login, no MSAL, no shell
  if (pathname.startsWith("/sign/")) {
    return <SignAgreement />;
  }

  if (!msalInitialized || !isAuthChecked) {
    return <div>Loading...</div>;
  }

  if (!user || !tenantId) {
    return <SignIn onLogin={handleLogin} />;
  }

  return (
    <TabProvider>
      <AuthedShell
        user={user}
        pathname={pathname}
        companyProfile={companyProfile}
        notifications={notifications}
        activeNotificationTab={activeNotificationTab}
        setActiveNotificationTab={setActiveNotificationTab}
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
        desktopSidebarOpen={desktopSidebarOpen}
        setDesktopSidebarOpen={setDesktopSidebarOpen}
        markAsRead={markAsRead}
        getNotificationIcon={getNotificationIcon}
        formatRelativeTime={formatRelativeTime}
        handleLogout={handleLogout}
      />
    </TabProvider>
  );
}

// ── Authenticated shell: sidebar, header, notifications, tabbed workspace ──
function AuthedShell({
  user,
  companyProfile,
  notifications,
  activeNotificationTab,
  setActiveNotificationTab,
  mobileSidebarOpen,
  setMobileSidebarOpen,
  desktopSidebarOpen,
  setDesktopSidebarOpen,
  markAsRead,
  pathname, // ← receive as prop
  getNotificationIcon,
  formatRelativeTime,
  handleLogout,
}) {
  const { openTab, tabs, activeId } = useTabs(); // no useLocation here
  const activePath = tabs.find((t) => t.id === activeId)?.path || "";

  // Handle notification click → opens (or focuses) a tab
  const handleNotificationClick = (notification) => {
    if (!notification.clickable) {
      markAsRead(notification._id);
      return;
    }

    markAsRead(notification._id);
    const { category, entityType, entityId, projectId } = notification;

    if (!projectId) {
      openTab("/Projects");
      return;
    }

    let route = `/Projects/${projectId._id}`;
    let state = { initialTab: "General" };

    switch (category) {
      case "materials":
        route = `/projects/${projectId._id}/material-order/${entityId}`;
        break;
      case "rfi":
        state = { initialTab: "Request for Information (RFI)" };
        break;
      case "change_orders":
        route = `/projects/${projectId._id}/change-orders/${entityId}`;
        break;
      case "time_and_materials":
        state = { initialTab: "Time & Material" };
        break;
      default:
        break;
    }

    openTab(route, state);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  const filteredNotifications =
    activeNotificationTab === "Unread"
      ? notifications.filter((n) => !n.read)
      : notifications;

  return (
    <div className="bg-gray-100 min-h-screen">
      <Dialog
        open={mobileSidebarOpen}
        onClose={setMobileSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
        />
        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                <button
                  type="button"
                  onClick={() => setMobileSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-700 px-6 pb-4">
              <div className="flex h-16 shrink-0 items-center">
                <img
                  alt="Your Company"
                  src={companyProfile?.logo || "https://via.placeholder.com/32"}
                  className="h-8 w-auto"
                />
              </div>
              <nav className="flex flex-1 flex-col">
                <SidebarItems user={user} activePath={activePath} open={true} />
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col ${
          desktopSidebarOpen ? "lg:w-72" : "lg:w-20"
        }`}
      >
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-700 px-6 pb-4">
          <div
            className={`flex h-16 shrink-0 -ml-4 ${
              desktopSidebarOpen ? "-ml-4" : "-mr-4"
            }`}
          >
            <img
              alt="Your Company"
              src={logo}
              className={`w-auto ${desktopSidebarOpen ? "lg:h-20" : "lg:h-16"}`}
            />
          </div>
          <nav className="flex flex-1 flex-col">
            <SidebarItems
              user={user}
              activePath={activePath}
              open={desktopSidebarOpen}
            />
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li className="mt-auto">
                <button
                  onClick={() => setDesktopSidebarOpen((prev) => !prev)}
                  className="group -mx-2 mt-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-300 hover:bg-gray-600 hover:text-white"
                >
                  {desktopSidebarOpen ? (
                    <ChevronLeftIcon
                      aria-hidden="true"
                      className="size-6 shrink-0 text-gray-300 group-hover:text-white"
                    />
                  ) : (
                    <ChevronRightIcon
                      aria-hidden="true"
                      className="size-6 shrink-0 text-gray-300 group-hover:text-white"
                    />
                  )}
                  {desktopSidebarOpen && "Hide"}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <div>
        <div className="sticky top-0 z-[10] flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>

          <div
            aria-hidden="true"
            className="h-6 w-px bg-gray-900/10 lg:hidden"
          />

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <Menu as="div" className="relative">
                <MenuButton className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                  <span className="sr-only">View notifications</span>
                  <div className="relative">
                    <BellIcon aria-hidden="true" className="size-6" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2 w-96 origin-top-right rounded-xl bg-gray-700 py-2 shadow-xl ring-1 ring-gray-600 focus:outline-none transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                >
                  <div className="flex border-b border-gray-600 mb-2">
                    <button
                      onClick={() => setActiveNotificationTab("All")}
                      className={classNames(
                        activeNotificationTab === "All"
                          ? "border-b-2 border-orange-400 text-orange-400"
                          : "text-gray-400 hover:text-white hover:bg-gray-600",
                        "flex-1 py-1.5 px-4 text-sm font-semibold text-center inline-block transition-colors duration-200",
                      )}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setActiveNotificationTab("Unread")}
                      className={classNames(
                        activeNotificationTab === "Unread"
                          ? "border-b-2 border-orange-400 text-orange-400"
                          : "text-gray-400 hover:text-white hover:bg-gray-600",
                        "flex-1 py-1.5 px-4 text-sm font-semibold text-center inline-block transition-colors duration-200",
                      )}
                    >
                      Unread
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-700 scrollbar-thumb-rounded">
                    {filteredNotifications.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-400 text-center">
                        No {activeNotificationTab.toLowerCase()} notifications
                      </div>
                    ) : (
                      filteredNotifications.map((notif) => (
                        <MenuItem key={notif._id}>
                          <div
                            onClick={() => handleNotificationClick(notif)}
                            className={classNames(
                              notif.read
                                ? "bg-gray-700 text-gray-400"
                                : "bg-gray-600 text-gray-200",
                              notif.clickable
                                ? "hover:bg-gray-600 cursor-pointer"
                                : "cursor-default",
                              "flex items-center gap-x-3 px-4 py-3 text-sm border-b border-gray-600 last:border-b-0 transition-colors duration-150",
                            )}
                          >
                            {getNotificationIcon(notif.category, notif.read)}
                            <div className="flex-1">
                              <p className="font-medium truncate">
                                {notif.category === "materials" &&
                                notif.materialOrderNumber
                                  ? `${notif.title} - #${notif.materialOrderNumber}`
                                  : notif.category === "rfi" && notif.rfiNumber
                                    ? `${notif.title} - #${notif.rfiNumber}`
                                    : notif.category === "time_and_materials" &&
                                        notif.timeAndMaterialNumber
                                      ? `${notif.title} - #${notif.timeAndMaterialNumber}`
                                      : notif.category === "change_orders" &&
                                          notif.changeOrderNumber
                                        ? `${notif.title} - #${notif.changeOrderNumber}`
                                        : notif.title}
                              </p>
                              {notif.projectName && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {notif.projectName}
                                </p>
                              )}
                              <p className="text-xs text-orange-400 mt-1">
                                {formatRelativeTime(notif.createdAt)}
                              </p>
                            </div>
                          </div>
                        </MenuItem>
                      ))
                    )}
                  </div>
                </MenuItems>
              </Menu>

              <div
                aria-hidden="true"
                className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"
              />

              <Menu as="div" className="relative">
                <MenuButton className="relative flex items-center">
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">Open user menu</span>
                  <img
                    alt=""
                    src={
                      companyProfile?.logo || "https://via.placeholder.com/32"
                    }
                    className="size-12 bg-black rounded-full py-1"
                  />
                  <span className="hidden lg:flex lg:items-center">
                    <span
                      aria-hidden="true"
                      className="ml-4 text-sm/6 font-semibold text-gray-900"
                    >
                      {user?.displayName}
                    </span>
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="ml-2 size-5 text-gray-400"
                    />
                  </span>
                </MenuButton>
                <MenuItems
                  transition
                  className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-hidden data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-in"
                >
                  {userNavigation(user, handleLogout).map((item) => (
                    <MenuItem key={item.name}>
                      <button
                        onClick={
                          item.onClick ? item.onClick : () => openTab(item.href)
                        }
                        className={classNames(
                          item.name === "Sign out"
                            ? "text-red-600"
                            : "text-gray-900",
                          "block w-full text-left px-3 py-1 text-sm/6 data-focus:bg-gray-50 data-focus:outline-hidden",
                        )}
                      >
                        {item.name}
                      </button>
                    </MenuItem>
                  ))}
                </MenuItems>
              </Menu>
            </div>
          </div>
        </div>

        <main
          className={`transition-all duration-300 ${desktopSidebarOpen ? "lg:ml-72 lg:px-8" : "lg:ml-20 lg:px-4"} px-2`}
        >
          <div className="px-2 sm:px-4 lg:px-6">
            {user?.role === "unassigned" ? (
              <h1 className="text-2xl">...</h1>
            ) : pathname === "/pdfcondition" ? (
              <ConditionPDFtoExcel />
            ) : (
              <TabbedWorkspace user={user} companyProfile={companyProfile} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarItems({ user, activePath, open }) {
  const { openTab } = useTabs();

  const [timezone, setTimezone] = useState(
    localStorage.getItem("userTimezone") || "UTC",
  );
  const timezones = [
    "UTC",
    "America/Toronto",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "Europe/London",
    "Asia/Tokyo",
    "Australia/Sydney",
  ];

  const handleTimezoneChange = (e) => {
    const newTimezone = e.target.value;
    setTimezone(newTimezone);
    localStorage.setItem("userTimezone", newTimezone);
  };

  const salesItems = [
    { name: "Prospects", href: "/Prospects", icon: FiCoffee },
    { name: "Leads", href: "/Leads", icon: FiFolderPlus },
    { name: "Opportunities", href: "/Opportunities", icon: FiEdit },
    { name: "Contacts", href: "/contacts", icon: FiUser },
    { name: "Accounts", href: "/accounts", icon: FiBriefcase },
  ];

  const operationsItems = [
    { name: "Projects", href: "/Projects", icon: FiHome },
    { name: "Takeoff", href: "/takeoff", icon: FiMap },
    { name: "Timesheets", href: "/timesheets", icon: FiClock },
    { name: "Worksheets", href: "/pieceworkerlog", icon: FiTable },
    { name: "Bills", href: "/bills", icon: FiFileText },
    { name: "Database", href: "/item-database", icon: FiDatabase },
    { name: "Inventory", href: "/inventory", icon: FiCodesandbox },
    { name: "Labour items", href: "/labour-items", icon: FiTool },
    { name: "Subcontractors", href: "/subagreements", icon: FiPenTool },
  ];

  const systemItems = [
    { name: "Payroll", href: "/payroll", icon: FiCreditCard },
    { name: "Overhead", href: "/overhead", icon: FiPieChart },
    { name: "Finance Dashboard", href: "/finance-dashboard", icon: FiBarChart },
  ];

  // Separate page from PayrollView: weekly submission + vacation + reimbursements
  const submitItems = [
    { name: "Submit Payroll", href: "/payroll-submission", icon: FiSend },
  ];

  const userItems = [{ name: "Settings", href: "/users", icon: FiSettings }];

  // Exact-segment match so "/payroll-submission" doesn't also light up "/payroll"
  const isCurrent = (href) =>
    activePath === href ||
    activePath.startsWith(`${href}/`) ||
    activePath.startsWith(`${href}?`);

  const filteredOperationsItems =
    user?.role === "foreman"
      ? operationsItems.filter((item) => item.name === "Projects")
      : operationsItems;

  const computedSales = salesItems.map((item) => ({
    ...item,
    current: activePath.startsWith(item.href),
  }));

  const computedOperations = filteredOperationsItems.map((item) => ({
    ...item,
    current: activePath.startsWith(item.href),
  }));

  const computedSystem = systemItems.map((item) => ({
    ...item,
    current: isCurrent(item.href),
  }));

  const computedSubmit = submitItems.map((item) => ({
    ...item,
    current: isCurrent(item.href),
  }));

  const computedUsers = userItems.map((item) => ({
    ...item,
    current: activePath.startsWith(item.href),
  }));

  // Admin: Submit Payroll + system pages + Settings. PM: Submit Payroll + Settings.
  const systemNav =
    user?.role === "admin"
      ? [...computedSubmit, ...computedSystem, ...computedUsers]
      : user?.role === "project_manager"
        ? [...computedSubmit, ...computedUsers]
        : [];

  const linkClass = (current) =>
    classNames(
      current
        ? "bg-orange-600 text-white"
        : "text-gray-300 hover:text-white hover:bg-gray-600",
      "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold w-full text-left",
    );

  const iconClass = (current) =>
    classNames(
      current ? "text-white" : "text-gray-300 group-hover:text-white",
      "size-6 shrink-0",
    );

  return (
    <ul role="list" className="flex flex-1 flex-col gap-y-7 mt-2">
      {/* Sales Section (hidden for foreman) */}
      {user?.role !== "foreman" && (
        <li>
          {open && (
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 flex items-center"
              >
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 text-sm font-semibold text-white bg-gray-700">
                  Sales
                </span>
              </div>
            </div>
          )}
          <ul role="list" className="-mx-2 mt-2 space-y-1">
            {computedSales.map((item) => (
              <li key={item.name}>
                <button
                  onClick={() => openTab(item.href)}
                  className={linkClass(item.current)}
                >
                  <item.icon
                    className={iconClass(item.current)}
                    aria-hidden="true"
                  />
                  {open && item.name}
                </button>
              </li>
            ))}
          </ul>
        </li>
      )}

      {/* Operations Section */}
      <li>
        {open && (
          <div className="relative mb-4">
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center"
            >
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 text-sm font-semibold text-white bg-gray-700">
                Operations
              </span>
            </div>
          </div>
        )}
        <ul role="list" className="-mx-2 mt-2 space-y-1">
          {computedOperations.map((item) => (
            <li key={item.name}>
              <button
                onClick={() => openTab(item.href)}
                className={linkClass(item.current)}
              >
                <item.icon
                  className={iconClass(item.current)}
                  aria-hidden="true"
                />
                {open && item.name}
              </button>
            </li>
          ))}
        </ul>
      </li>

      {/* System Section (hidden for foreman) */}
      {user?.role !== "foreman" && (
        <li>
          {open && (
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 flex items-center"
              >
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 text-sm font-semibold text-white bg-gray-700">
                  System
                </span>
              </div>
            </div>
          )}
          {systemNav.length > 0 && (
            <ul role="list" className="-mx-2 mt-2 space-y-1">
              {systemNav.map((item) => (
                <li key={item.name}>
                  <button
                    onClick={() => openTab(item.href)}
                    className={linkClass(item.current)}
                  >
                    <item.icon
                      className={iconClass(item.current)}
                      aria-hidden="true"
                    />
                    {open && item.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {open && (
            <div className="text-sm font-semibold text-white mt-4">
              Time Zone
            </div>
          )}
          {open && (
            <div className="-mx-2 mt-2 space-y-1">
              <div className="px-2">
                <select
                  value={timezone}
                  onChange={handleTimezoneChange}
                  className="block w-full rounded-md border-0 bg-gray-600 py-1.5 text-white shadow-sm ring-1 ring-inset ring-gray-500 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                >
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </li>
      )}
    </ul>
  );
}

function userNavigation(user, handleLogout) {
  const navigationItems = [
    { name: "Sign out", href: "#", onClick: handleLogout },
  ];

  if (user?.role === "admin" || user?.role === "project_manager") {
    navigationItems.unshift({ name: "Settings", href: `/users` });
    navigationItems.unshift({
      name: "Submit Payroll",
      href: `/payroll-submission`,
    });
  }

  return navigationItems;
}
